<script>
  import Icon from '@ui/pico/icon.svelte';
  import Protect from '@ui/pico/protect.svelte';
  import { onMount } from 'svelte';
  import { app, displayName, user, ux } from '../store';
  import { defaults, pages } from '../config';
  import { goto, navSupport } from '../util';
  import Help from '../pages/components/help.svelte';
  import { getUserAccount } from '../services';

  // @TODO: remove routes from this page since it causes circular dependency
  // $: siteTitle = getSiteTitle(app, routes);
  $: siteTitle = 'Webforma (alpha)';

  function toggleMode() {
    $ux.mode = $ux.mode === 'dark' ? 'light' : 'dark';
    document.querySelector('html').setAttribute('data-theme', $ux.mode);
  }

  document.onkeyup = navSupport(app);

  $: chargeupHref = $user?.isAnonymous
    ? pages.entry.link.url
    : pages.chargeups.url;

  // TODO: anon status shouldn't matter (should check if first site exists)
  $: addSiteHref = $user?.isAnonymous
    ? pages.sites.first.url
    : pages.sites.add.url;

  $: menuItems = [
    {
      name: 'Dashboard',
      protect: true,
      class: 'secondary outline',
      href: pages.dashboard.url,
      icon: 'grid',
      tooltip: pages.dashboard.description,
    },
    {
      name: 'Components',
      protect: false,
      class: 'secondary outline',
      href: pages.components.url,
      icon: 'code',
      tooltip: pages.components.description,
    },
    {
      name: 'Charge Ups',
      protect: true,
      class: 'secondary outline',
      href: chargeupHref,
      icon: 'credit-card',
      tooltip: pages.chargeups.description,
    },
  ];

  let showHelp = false;
  function showHelpDialog(ev) {
    try {
      // remove the tooltip focus if possible
      ev.target.parentElement.parentElement.blur();
      ev.target.blur();
    } catch (_) {}
    showHelp = true;
  }

  onMount(async () => {
    if ($user) {
      // get the user account for the first time and save to persistent store
      getUserAccount({ aid: $user?.uid });
    }
  });
</script>

<svelte:head>
  <title>{siteTitle}</title>
</svelte:head>

<Help open={showHelp} on:canceled={() => (showHelp = false)} />

<!-- top nav menu -->
<nav>
  <div on:keyup={null} on:click={() => goto('dashboard')}>
    <Icon size={48} name="box">Webforma</Icon>
  </div>
  <div />
  <Protect authStore={user}>
    <div
      class="lia"
      data-tooltip="Administrator View"
      data-placement="left"
      on:keyup={null}
      on:click={() => goto(pages.logout.url)}
    >
      {$displayName || $user?.email || defaults.guestAccountLabel}
      <Icon name="log-out" size={28} />
    </div>
    <div
      slot="auth:login"
      class="lia"
      data-tooltip="You are logged in with an anonymous account."
      data-placement="left"
    >
      {defaults.guestAccountLabel}
      <Icon name="log-out" size={28} />
    </div>
  </Protect>
</nav>

<aside>
  <!-- sidemenu -->
  {#each menuItems as item}
    {#if item.protect}
      <Protect authStore={user}>
        <button
          class={item.class}
          on:click={() => goto(item.href)}
          data-tooltip={item.tooltip}
          data-placement="right"
        >
          <Icon name={item.icon} size={28} />
        </button>
      </Protect>
    {:else}
      <button
        class={item.class}
        on:click={() => goto(item.href)}
        data-tooltip={item.tooltip}
        data-placement="right"
      >
        <Icon name={item.icon} size={28} />
      </button>
    {/if}
  {/each}

  <!-- bottom action menu -->
  <span class="bottom">
    <button
      class="contrast help"
      name="help-button"
      data-placement="right"
      data-tooltip="Get help from the Webforma Team!"
      on:click|stopPropagation={showHelpDialog}
    >
      <Icon name="help-circle" size={82} once:click|preventDefault />
    </button>
    <button
      class="secondary outline"
      on:click={toggleMode}
      data-placement="right"
      data-tooltip="Switch view mode dark/light"
    >
      {#if $ux.mode === 'dark'}
        <Icon name="sun" size={28} />
      {:else}
        <Icon name="moon" size={28} />
      {/if}
    </button>
    {#if $user?.email}
      <Protect authStore={user}>
        <button
          class="secondary outline danger"
          data-tooltip={pages.logout.description}
          data-placement="right"
          on:click={() => goto(pages.logout.url)}
        >
          <Icon name="log-out" size={28} />
        </button>
        <span slot="auth:login">
          <button
            class="secondary outline danger"
            on:click={() => goto(pages.entry.url)}
            data-tooltip={pages.entry.description}
            data-placement="right"
          >
            <Icon name="log-in" size={28} />
          </button>
        </span>
      </Protect>
    {:else}
      <button
        class="secondary outline danger"
        on:click={() => goto(pages.entry.link.url)}
        data-tooltip="Solidify your account by linking it (takes seconds)!"
        data-placement="right"
      >
        <Icon name="log-in" size={28} />
      </button>
    {/if}
  </span>
</aside>

<main class="container">
  <slot />
</main>

<style>
  :global(:root) {
    --danger: maroon;
    --highlight: royalblue;
    --padding-top: 6rem;
    --stroke-color: white;
  }
  :global([data-theme='dark']) {
    --stroke-color: var(--secondary);
  }
  :global(body) {
    display: grid;
    grid-template-rows: 100px auto;
    /* grid-template-columns: 100px auto; */
    height: 100vh;
  }
  :global(button.help) {
    display: flex;
    justify-content: center;
    align-items: center;
  }
  :global(button.help svg) {
    stroke: var(--stroke-color);
    padding: 0 !important;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
  }

  aside {
    display: flex;
    flex-direction: column;
    height: calc(100vh - var(--padding-top));
    margin: var(--padding-top);
    margin-left: 1em;
    position: fixed;
  }
  aside button {
    /* background: rgba(0, 0, 0, 0.2); */
    margin-bottom: 0.4rem;
    padding: 1.2rem;
    max-width: 80px;
  }
  :global(aside button svg) {
    color: var(--secondary);
    font-weight: 900;
    stroke-width: 1.2px;
  }
  :global(aside button:hover svg) {
    color: var(--contrast);
  }
  aside .bottom {
    margin-top: auto;
  }
  /* button.danger:hover {
    background: var(--danger);
  } */
  aside button:hover {
    /* background: var(--highlight); */
    cursor: pointer;
  }
  /* aside button:active {
    background: rgba(255, 255, 255, 0.4);
  } */

  nav {
    z-index: 2;
    font-weight: 900;
    font-size: 1.8em;
    align-items: center;
    padding: 0.4em 1em 0.6em;
    position: fixed;
    width: 100%;
    text-align: center;
    cursor: pointer;
    background: black;
    color: white;
    /* background: rgba(0, 0, 0, 0.4); */
    /* filter: drop-shadow(2rem 0 4rem skyblue); */
  }

  main {
    height: fit-content;
    padding-top: var(--padding-top);
    width: 60vw;
  }
</style>
